import { useEffect } from "react";
import {
  RealtimeKitProvider,
  useRealtimeKitClient,
  useRealtimeKitMeeting,
  useRealtimeKitSelector,
} from "@cloudflare/realtimekit-react";
import { RTK_HOST_PRESET } from "./realtimeKitPresets";
import { LivestreamHostView } from "./LivestreamHostView";
import { LivestreamViewerView } from "./LivestreamViewerView";
import type { MeetingSessionExperienceProps } from "./meetingProductTypes";

function LivestreamMeetingInner({
  shopProductIds,
  leadContact,
  showLogout,
  loggingOut,
  onLogout,
}: Omit<MeetingSessionExperienceProps, "authToken">) {
  const { meeting } = useRealtimeKitMeeting();
  const roomState = useRealtimeKitSelector((m) => m.self.roomState);
  const isHost = useRealtimeKitSelector(
    (m) => m.self.presetName === RTK_HOST_PRESET,
  );

  if (!meeting || roomState !== "joined") {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-black text-white">
        Connecting...
      </div>
    );
  }

  if (isHost) {
    return (
      <LivestreamHostView
        showLogout={showLogout}
        loggingOut={loggingOut}
        onLogout={onLogout}
      />
    );
  }

  return (
    <LivestreamViewerView
      shopProductIds={shopProductIds}
      leadContact={leadContact}
      showLogout={showLogout}
      loggingOut={loggingOut}
      onLogout={onLogout}
    />
  );
}

function LivestreamMeetingExperienceInner({
  authToken,
  shopProductIds,
  leadContact,
  showLogout,
  loggingOut,
  onLogout,
}: MeetingSessionExperienceProps) {
  const [meetingClient, initMeeting] = useRealtimeKitClient();

  useEffect(() => {
    initMeeting({
      authToken,
      defaults: { audio: false, video: false },
    });
  }, [authToken, initMeeting]);

  useEffect(() => {
    if (!meetingClient) return;
    void meetingClient.join();
  }, [meetingClient]);

  return (
    <RealtimeKitProvider value={meetingClient}>
      <LivestreamMeetingInner
        shopProductIds={shopProductIds}
        leadContact={leadContact}
        showLogout={showLogout}
        loggingOut={loggingOut}
        onLogout={onLogout}
      />
    </RealtimeKitProvider>
  );
}

export function LivestreamMeetingExperience(props: MeetingSessionExperienceProps) {
  return <LivestreamMeetingExperienceInner {...props} />;
}
